<template>
    <main class="flex-1">
        <div class="py-6">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                <div class="flex justify-between items-center">
                    <h1 class="text-2xl font-semibold text-gray-900">Správa cien</h1>
                    <div class="flex space-x-3">
                        <router-link to="/price-categories"
                            class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M7 7h.01M7 3h5c.512 0 1.023.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z">
                                </path>
                            </svg>
                            Kategórie cien
                        </router-link>
                        <button @click="handleAddPrice"
                            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                            Pridať cenu
                        </button>
                    </div>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mb-4">
                <!-- No tabs needed anymore -->
            </div>

            <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                <div class="py-4">
                    <!-- Filter by category -->
                    <div class="flex space-x-4 my-4">
                        <button v-for="category in categories" :key="category.key"
                            @click="selectedCategory = category.key" :class="[
                                'px-3 py-2 text-sm font-medium rounded-md',
                                selectedCategory === category.key
                                    ? 'bg-teal-600 text-white'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                            ]">
                            {{ category.name }}
                        </button>
                    </div> <!-- Loading state -->
                    <div v-if="loading" class="text-center py-8">
                        <div
                            class="w-10 h-10 mx-auto rounded-full border-3 border-teal-100 border-t-teal-600 animate-spin">
                        </div>
                        <p class="mt-2 text-sm text-gray-500">Načítavam ceny...</p>
                    </div>

                    <!-- Error state -->
                    <div v-else-if="error" class="bg-red-50 p-4 rounded-md">
                        <div class="flex">
                            <div class="flex-shrink-0">
                                <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                                    fill="currentColor" aria-hidden="true">
                                    <path fill-rule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                        clip-rule="evenodd" />
                                </svg>
                            </div>
                            <div class="ml-3">
                                <h3 class="text-sm font-medium text-red-800">
                                    Chyba pri načítaní cien
                                </h3>
                                <div class="mt-2 text-sm text-red-700">
                                    <p>{{ error }}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Price list -->
                    <div v-else class="mt-8 flex flex-col">
                        <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                            <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                                <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                                    <table class="min-w-full divide-y divide-gray-300">
                                        <thead class="bg-gray-50">
                                            <tr>
                                                <th scope="col"
                                                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                                    Názov
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                    Kľúč
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                    Kategória
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                    Bežná cena
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                    Akciová cena
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                    Stav
                                                </th>
                                                <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6">
                                                    <span class="sr-only">Akcie</span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody class="divide-y divide-gray-200 bg-white">
                                            <tr v-for="price in filteredPrices" :key="price.id"
                                                :class="{ 'bg-gray-50': !price.isActive }">
                                                <td
                                                    class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                                    {{ price.name }}
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {{ price.key }}
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {{ price.category }}
                                                    <span v-if="price.subcategory" class="text-xs text-gray-400">
                                                        ({{ price.subcategory }})
                                                    </span>
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {{ formatPrice(price.normalPrice) }}
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    <span v-if="price.salePrice !== null"
                                                        class="text-red-600 font-medium">
                                                        {{ formatPrice(price.salePrice) }}
                                                    </span>
                                                    <span v-else>-</span>
                                                </td>
                                                <td class="whitespace-nowrap px-3 py-4 text-sm">
                                                    <span :class="price.isActive
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-gray-100 text-gray-800'"
                                                        class="inline-flex rounded-full px-2 text-xs font-semibold leading-5">
                                                        {{ price.isActive ? 'Aktívna' : 'Neaktívna' }}
                                                    </span>
                                                </td>
                                                <td
                                                    class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                                    <button @click="handleEditPrice(price)"
                                                        class="text-teal-600 hover:text-teal-900 mr-4">
                                                        Upraviť
                                                    </button>
                                                    <button @click="handleDuplicatePrice(price)"
                                                        class="text-blue-600 hover:text-blue-900 mr-4">
                                                        Duplikovať
                                                    </button>
                                                    <button @click="handleToggleStatus(price)" :class="price.isActive
                                                        ? 'text-red-600 hover:text-red-900'
                                                        : 'text-green-600 hover:text-green-900'" class="mr-4">
                                                        {{ price.isActive ? 'Deaktivovať' : 'Aktivovať' }}
                                                    </button>
                                                    <button @click="handleDeletePrice(price)"
                                                        class="text-red-600 hover:text-red-900">
                                                        Vymazať
                                                    </button>
                                                </td>
                                            </tr>
                                            <!-- No prices message -->
                                            <tr v-if="filteredPrices.length === 0">
                                                <td colspan="7" class="px-3 py-8 text-sm text-center text-gray-500">
                                                    Žiadne ceny neboli nájdené pre vybratú kategóriu
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Price Form Modal -->
        <transition enter-active-class="ease-out duration-300" enter-from-class="opacity-0" enter-to-class="opacity-100"
            leave-active-class="ease-in duration-200" leave-from-class="opacity-100" leave-to-class="opacity-0">
            <div v-if="showModal" class="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog"
                aria-modal="true">
                <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

                    <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                    <div
                        class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                        <div class="absolute top-0 right-0 pt-4 pr-4">
                            <button type="button"
                                class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                                @click="showModal = false">
                                <span class="sr-only">Close</span>
                                <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                    stroke="currentColor" aria-hidden="true">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div class="sm:flex sm:items-start">
                            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                    {{ editMode ? 'Upraviť cenu' : (duplicateMode ? 'Duplikovať cenu' : 'Pridať novú cenu') }}
                                </h3>
                                <div class="mt-4">
                                    <!-- Price form -->
                                    <form @submit.prevent="submitPriceForm">
                                        <!-- Name -->
                                        <div class="mb-4">
                                            <label for="name" class="block text-sm font-medium text-gray-700">
                                                Názov
                                            </label>
                                            <input type="text" id="name" v-model="priceForm.name"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                                                required />
                                        </div> <!-- Key -->
                                        <div class="mb-4">
                                            <label for="key" class="block text-sm font-medium text-gray-700">
                                                Kľúč
                                            </label>
                                            <div class="relative"> <input type="text" id="key" v-model="priceForm.key"
                                                    class="mt-1 block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                                                    :class="{
                                                        'bg-gray-100 cursor-not-allowed border-gray-300': editMode,
                                                        'border-blue-400 bg-blue-50': duplicateMode,
                                                        'border-gray-300': !editMode && !duplicateMode
                                                    }" :disabled="editMode" required
                                                    :title="editMode ? 'Kľúč nie je možné upraviť pri editácii existujúcej ceny kvôli zachovaniu integrity údajov' : ''" />
                                                <div v-if="editMode"
                                                    class="absolute inset-y-0 right-0 flex items-center pr-3">
                                                    <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z">
                                                        </path>
                                                    </svg>
                                                </div>
                                                <div v-else-if="duplicateMode"
                                                    class="absolute inset-y-0 right-0 flex items-center pr-3">
                                                    <svg class="h-4 w-4 text-blue-500" fill="none" stroke="currentColor"
                                                        viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round"
                                                            stroke-width="2"
                                                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z">
                                                        </path>
                                                    </svg>
                                                </div>
                                            </div>
                                            <p class="mt-1 text-xs text-gray-500">
                                                <span v-if="editMode" class="text-amber-600">
                                                    ⚠️ Kľúč nie je možné upraviť pri editácii existujúcej ceny
                                                </span>
                                                <span v-else-if="duplicateMode" class="text-blue-600">
                                                    🔄 POZOR: Zmeňte kľúč na unikátny.
                                                </span>
                                                <span v-else>
                                                    Unikátny identifikátor pre cenu (napr. "company-foundation")
                                                </span>
                                            </p>
                                        </div><!-- Category -->
                                        <div class="mb-4">
                                            <label for="category" class="block text-sm font-medium text-gray-700">
                                                Kategória
                                            </label>
                                            <select id="category" v-model="selectedCategoryName"
                                                @change="onCategoryChange"
                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                                                required>
                                                <option value="">Vyberte kategóriu</option>
                                                <option v-for="category in priceCategories" :key="category.id"
                                                    :value="category.name">
                                                    {{ category.name }}
                                                </option>
                                            </select>
                                            <p class="mt-1 text-xs text-gray-500">
                                                Vyberte existujúcu kategóriu. Pre vytvorenie novej kategórie použite
                                                správu kategórií.
                                            </p>
                                        </div>

                                        <!-- Subcategory -->
                                        <div class="mb-4" v-if="availableSubcategories.length > 0">
                                            <label for="subcategory" class="block text-sm font-medium text-gray-700">
                                                Podkategória (voliteľné)
                                            </label>
                                            <select id="subcategory" v-model="priceForm.subcategory"
                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm">
                                                <option value="">Bez podkategórie</option>
                                                <option v-for="subcategory in availableSubcategories" :key="subcategory"
                                                    :value="subcategory">
                                                    {{ subcategory }}
                                                </option>
                                            </select>
                                            <p class="mt-1 text-xs text-gray-500">
                                                Vyberte podkategóriu z dostupných možností pre vybratú kategóriu.
                                            </p>
                                        </div>

                                        <!-- Description -->
                                        <div class="mb-4">
                                            <label for="description" class="block text-sm font-medium text-gray-700">
                                                Popis
                                            </label>
                                            <textarea id="description" v-model="priceForm.description" rows="3"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                                                required></textarea>
                                        </div>

                                        <!-- Normal Price -->
                                        <div class="mb-4">
                                            <label for="normalPrice" class="block text-sm font-medium text-gray-700">
                                                Bežná cena (€)
                                            </label>
                                            <input type="number" step="0.01" id="normalPrice"
                                                v-model="priceForm.normalPrice"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                                                required />
                                        </div>

                                        <!-- Sale Price -->
                                        <div class="mb-4">
                                            <label for="salePrice" class="block text-sm font-medium text-gray-700">
                                                Akciová cena (€, voliteľné)
                                            </label>
                                            <input type="number" step="0.01" id="salePrice"
                                                v-model="priceForm.salePrice"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm" />
                                            <p class="mt-1 text-xs text-gray-500">
                                                Nechajte prázdne, ak nie je v akcii
                                            </p>
                                        </div>

                                        <!-- Priority -->
                                        <div class="mb-4">
                                            <label for="priority" class="block text-sm font-medium text-gray-700">
                                                Priorita zobrazenia (voliteľné)
                                            </label>
                                            <input type="number" id="priority" v-model="priceForm.priority"
                                                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm" />
                                            <p class="mt-1 text-xs text-gray-500">
                                                Vyššia priorita (vyššie číslo) = zobrazí sa skôr v zozname
                                            </p>
                                        </div>

                                        <!-- Active status -->
                                        <div class="mb-6">
                                            <div class="flex items-center">
                                                <input id="isActive" type="checkbox" v-model="priceForm.isActive"
                                                    class="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded" />
                                                <label for="isActive" class="ml-2 block text-sm text-gray-900">
                                                    Aktívna (zobrazuje sa zákazníkom)
                                                </label>
                                            </div>
                                        </div>

                                        <!-- Form actions -->
                                        <div class="flex justify-end mt-6 space-x-3">
                                            <button type="button"
                                                class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                                                @click="showModal = false">
                                                Zrušiť
                                            </button>
                                            <button type="submit"
                                                class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                                                :disabled="formSubmitting">
                                                <span v-if="formSubmitting">Spracovanie...</span> <span v-else>{{
                                                    editMode ? 'Uložiť zmeny' : (duplicateMode ? 'Duplikovať cenu' :
                                                        'Pridať cenu') }}</span>
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Delete Confirmation Modal -->
        <transition enter-active-class="ease-out duration-300" enter-from-class="opacity-0" enter-to-class="opacity-100"
            leave-active-class="ease-in duration-200" leave-from-class="opacity-100" leave-to-class="opacity-0">
            <div v-if="showDeleteModal" class="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title"
                role="dialog" aria-modal="true">
                <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

                    <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                    <div
                        class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                        <div class="sm:flex sm:items-start">
                            <div
                                class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                <svg class="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none"
                                    viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                    Vymazať cenu
                                </h3>
                                <div class="mt-2">
                                    <p class="text-sm text-gray-500">
                                        Naozaj chcete vymazať cenu "{{ priceToDelete?.name }}"? Táto akcia je nevratná a
                                        môže ovplyvniť objednávky a produkty, ktoré používajú túto cenu.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                            <button type="button"
                                class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                                @click="confirmDelete" :disabled="deleteSubmitting">
                                {{ deleteSubmitting ? 'Vymazávanie...' : 'Vymazať' }}
                            </button>
                            <button type="button"
                                class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:mt-0 sm:w-auto sm:text-sm"
                                @click="showDeleteModal = false">
                                Zrušiť
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </main>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import PriceDataService from '@/services/PriceDataService';
import PriceCategoryService from '@/services/PriceCategoryService';
import type { Price } from '@/types';
import type { PriceCategory } from '@/services/PriceCategoryService';

// State
const prices = ref<Price[]>([]);
const loading = ref(true);
const error = ref<string | null>(null);

// Price form state
const showModal = ref(false);
const editMode = ref(false);
const duplicateMode = ref(false);
const formSubmitting = ref(false);
const priceForm = ref<Partial<Price>>({
    name: '',
    key: '',
    category: 'sro',
    subcategory: '',
    description: '',
    normalPrice: 0,
    salePrice: null,
    isActive: true,
    priority: 0,
});

// Delete confirmation state
const showDeleteModal = ref(false);
const priceToDelete = ref<Price | null>(null);
const deleteSubmitting = ref(false);

// Category filtering
const selectedCategory = ref('all');
const selectedCategoryName = ref('');
const priceCategories = ref<PriceCategory[]>([]);
const categories = computed(() => {
    return [
        { key: 'all', name: 'Všetky kategórie' },
        ...priceCategories.value.map(category => ({
            key: category.name,
            name: category.name.charAt(0).toUpperCase() + category.name.slice(1)
        }))
    ];
});

// Available subcategories for selected category
const availableSubcategories = computed(() => {
    if (!selectedCategoryName.value) {
        return [];
    }

    const category = priceCategories.value.find(cat => cat.name === selectedCategoryName.value);
    return category?.subcategories || [];
});

// Computed property for filtered prices
const filteredPrices = computed(() => {
    if (selectedCategory.value === 'all') {
        return prices.value;
    }
    return prices.value.filter(price => price.category === selectedCategory.value);
});

// Fetch all prices and categories on component mount
onMounted(async () => {
    await Promise.all([
        fetchPrices(),
        fetchCategories()
    ]);
});

// Methods
async function fetchPrices() {
    loading.value = true;
    error.value = null;

    try {
        const response = await PriceDataService.getAllPrices();
        const data = response.data.data || response.data;
        prices.value = Array.isArray(data) ? data : [];
    } catch (err) {
        console.error('Error fetching prices:', err);
        error.value = 'Nastala chyba pri načítaní cien. Skúste to prosím neskôr.';
    } finally {
        loading.value = false;
    }
}

// Fetch all categories
async function fetchCategories() {
    try {
        const response = await PriceCategoryService.getAllCategories();
        const data = response.data.data || response.data;
        priceCategories.value = Array.isArray(data) ? data : [];
    } catch (err) {
        console.error('Error loading categories:', err);
        // Don't set error state to avoid affecting the whole UI
    }
}

// Format price in EUR
function formatPrice(price: number): string {
    return new Intl.NumberFormat('sk-SK', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(price);
}

// Add new price handler
function handleAddPrice() {
    editMode.value = false;
    duplicateMode.value = false;
    resetPriceForm();
    showModal.value = true;
}

// Edit price handler
function handleEditPrice(price: Price) {
    editMode.value = true;
    duplicateMode.value = false;
    priceForm.value = { ...price };
    // Set the selected category name for the select dropdown
    selectedCategoryName.value = price.category || '';
    showModal.value = true;

    // Debug log to check if category is properly set
    console.log('Editing price:', price);
    console.log('Category from price:', price.category);
    console.log('Selected category name:', selectedCategoryName.value);
}

// Duplicate price handler
function handleDuplicatePrice(price: Price) {
    editMode.value = false; // Set to create mode (not edit)
    duplicateMode.value = true; // Set duplicate mode

    priceForm.value = {
        ...price,
        key: price.key, // Keep original key (user must manually change it)
        id: undefined // Remove the ID so it creates a new record
    };

    // Set the selected category name for the select dropdown
    selectedCategoryName.value = price.category || '';
    showModal.value = true;

    console.log('Duplicating price:', price);
    console.log('Original key preserved:', priceForm.value.key);
    console.log('Form data for duplicate:', priceForm.value);
}

// Handle category selection change
function onCategoryChange() {
    priceForm.value.category = selectedCategoryName.value;
    // Reset subcategory when category changes
    priceForm.value.subcategory = '';
}

// Toggle price active status
async function handleToggleStatus(price: Price) {
    try {
        await PriceDataService.togglePriceStatus(price.id, !price.isActive);
        await fetchPrices();
    } catch (err) {
        console.error('Error toggling price status:', err);
        alert('Nastala chyba pri zmene stavu ceny.');
    }
}

// Delete price handler
function handleDeletePrice(price: Price) {
    priceToDelete.value = price;
    showDeleteModal.value = true;
}

// Confirm delete action
async function confirmDelete() {
    if (!priceToDelete.value) return;

    deleteSubmitting.value = true;

    try {
        await PriceDataService.deletePrice(priceToDelete.value.id);
        showDeleteModal.value = false;
        await fetchPrices();
    } catch (err) {
        console.error('Error deleting price:', err);
        alert('Nastala chyba pri vymazávaní ceny.');
    } finally {
        deleteSubmitting.value = false;
        priceToDelete.value = null;
    }
}

// Reset form to default values
function resetPriceForm() {
    priceForm.value = {
        name: '',
        key: '',
        category: '',
        subcategory: '',
        description: '',
        normalPrice: 0,
        salePrice: null,
        isActive: true,
        priority: 0,
    };
    selectedCategoryName.value = '';
    duplicateMode.value = false;
}

// Submit price form (create or update)
async function submitPriceForm() {
    formSubmitting.value = true;

    try {
        if (editMode.value && priceForm.value.id) {
            await PriceDataService.updatePrice(priceForm.value.id, priceForm.value);
        } else {
            await PriceDataService.createPrice(priceForm.value);
        }

        showModal.value = false;
        await fetchPrices();
    } catch (err) {
        console.error('Error saving price:', err);

        // Handle specific validation errors
        if (err && typeof err === 'object' && 'response' in err) {
            const response = (err as { response?: { status?: number; data?: { error?: unknown } } }).response;
            if (response?.status === 422) {
                const errors = response.data?.error;
                if (errors && typeof errors === 'object' && 'key' in errors) {
                    const keyErrors = (errors as { key?: string[] }).key;
                    if (keyErrors && keyErrors[0]) {
                        alert(`Chyba: ${keyErrors[0]}`);
                        return;
                    }
                }
                alert('Chyba validácie údajov. Skontrolujte všetky polia.');
            } else if (response?.data && typeof response.data === 'object' && 'error' in response.data) {
                const errorMessage = (response.data as { error?: string }).error;
                if (errorMessage) {
                    alert(`Chyba: ${errorMessage}`);
                    return;
                }
            }
        }
        alert('Nastala chyba pri ukladaní ceny.');
    } finally {
        formSubmitting.value = false;
    }
}
</script>
